import { useRequest } from "~/api/Request"
import { useConfig } from "~/composable/useConfig"
import { UploadDocument, DocumentSendConfirm } from "~/model/Workflow"
import dayjs from "dayjs"
import React from "react"
import { Contract, ContractTypeTexts } from "~/model/Contract"
import TaskTag from "~/components/molecules/TaskTag"
import { User } from "~/model/User"
import { CompanyType } from "~/model/Company"
import { TableColumn } from "~/components/atoms/AcTable"
import { formatPrice } from "~/utils/functions"
import { SignContractWithListDocs, OnlineSignContract } from "~/model/Contract"
import { Params } from "~/api/Request"
import { ExTableDataSource } from "~/components/template/Contract"
import API from "~/api/api"
import API_V2 from "~/api/api-v2"
import { ContractDocument } from "~/model/ContractDocument"
import { VideoRecord } from "~/model/VideoRecord"
import { LedgerHistory } from "~/components/template/LedgerHistoryModal"

export function useContract() {
  const config = useConfig()
  const { Request } = useRequest()

  const onSave = async (contract: any) => {
    const { importantNote } = contract
    const files = Object.keys(importantNote.documents || {})
    const contractDocumentUrl = API.CONTRACT_DOCUMENT.replace("{contractId}", contract.id)
    const contractMeetingUrl = API.CONTRACT_MEETING.replace("{contractId}", contract.id)
    if (files.length) {
      const formData = new FormData()
      const blobs = await Promise.all(
        files.map(async (key) => {
          const localData = await fetch(importantNote.documents[key].data)
          const blob = await localData.blob()
          return blob
        })
      )
      files.map((key, index) => {
        formData.append(key, blobs[index], importantNote.documents[key].name)
      })
      await Request.put(contractDocumentUrl, formData, {
        headers: {
          "content-type": "multipart/form-data",
        },
      }).then((response) => response.data)
    }
    const result = await Request.put(contractMeetingUrl, {
      method: importantNote.method,
      meetingDate: importantNote.meetingDate,
      signatureType: importantNote.signatureType,
      signatureColumns: importantNote.signatureColumns,
      isSend: Boolean(importantNote.isSend),
    }).then((response) => response.data)
    return result
  }

  const saveMeetingDate = async (contractId: number, payload: Params) => {
    const contractMeetingUrl = API.CONTRACT_MEETING.replace("{contractId}", `${contractId}`)
    const response = await Request.put(contractMeetingUrl, payload)
    return response.data
  }

  const saveMeetingDateV2 = async (contractId: number, payload: Params) => {
    const contractMeetingUrl = API_V2.CONTRACT_MEETING.replace("{contractId}", `${contractId}`)
    const response = await Request.put(contractMeetingUrl, payload)
    return response.data
  }

  /**
   * 書類を保存
   * @param data
   */
  const saveDocument = async (data: UploadDocument, notRequireUpload = false) => {
    const formData = new FormData()
    const url = API.SAVE_CONTRACT_DOCUMENT.replace("{contractId}", `${data.contractId}`).replace(
      "{workflowId}",
      `${data.workflowId}`
    )
    if (data?.file?.id) {
      formData.append("document", data.fileBlob, encodeURI(data.file.name))
      formData.append("notRequireUpload", "0")
    } else if (notRequireUpload) {
      formData.append("notRequireUpload", "1")
    }
    return await Request.put(url, formData, {
      headers: {
        "content-type": "multipart/form-data",
      },
    }).then((response) => response.data)
  }

  /**
   * 書類を保存 type multi
   * @param data
   */
  const saveDocumentMulti = async (data: any) => {
    const body = new FormData()
    const url = API.SAVE_DOCUMENT_MULTI.replace("{contractId}", `${data.contractId}`).replace(
      "{workflowId}",
      `${data.workflowId}`
    )
    body.append("documents", data.fileBlob, encodeURI(data.fileBlob.name))
    return Request.put(url, body, {
      headers: {
        "content-type": "multipart/form-data",
      },
    })
  }

  /**
   * 書類の送信
   * @param contractId
   * @param ids
   */
  const sendDocument = async (contractId: number | string, ids: number[] | string[] | string) => {
    const sendDocumentUrl = API.SEND_DOCUMENT.replace("{contractId}", `${contractId}`)
    return await Request.post<boolean>(sendDocumentUrl, {
      ids: Array.isArray(ids) ? ids : [ids],
    }).then((response) => response.data)
  }

  const resendDocumentsV2 = async (ids: number[], contractId: number | string) => {
    const url = API_V2.RESEND_API.replace("{contractId}", `${contractId}`)
    const payload = { ids: ids }
    try {
      await Request.post(url, payload)
    } catch (error) {
      console.error(error)
    }
  }

  const getDocumentSend = async (contractId: string | string[], payload: Params) => {
    const sendDocumentUrl = API_V2.SEND_DOCUMENT.replace("{contractId}", `${contractId}`)
    const response = await Request.get<DocumentSendConfirm>(sendDocumentUrl, payload)
    return response.data
  }

  const reSendDocument = async (
    contractId: number | string,
    ids: number[] | string[] | string,
    idsDoc: number[] | string[] | string
  ) => {
    let newIds
    const resendUrl = API.CONTRACTOR_RESEND.replace("{contractId}", `${contractId}`)
    if (typeof idsDoc === "string") {
      newIds = Number(idsDoc)
    } else {
      newIds = idsDoc.map((i) => Number(i))
    }
    return await Request.post<boolean>(resendUrl, {
      ids: Array.isArray(newIds) ? newIds : [newIds],
    }).then((response) => response.data)
  }

  const getContractToken = async (configData: Params) => {
    const url = API_V2.CONTRACT_TOKEN
    return await Request.get<SignContractWithListDocs>(url, null, null, null, configData)
  }

  const updateResonaStatus = async (payload: Params, configData: Params) => {
    const url = API.RESONA_STATUS
    return await Request.put(url, payload, configData)
  }

  const remailResona = async (contractId: number) => {
    const url = API.RESONA_SENDMAIL.replace("{contractId}", `${contractId}`)
    return await Request.put(url)
  }

  const getContract = async (contractId: string | string[]) => {
    const url = API.CONTRACT.replace("{contractId}", `${contractId}`)
    const response = await Request.get<Contract>(url)
    return response.data
  }

  const getContractVideoRecords = async (contractId: string | string[]) => {
    const url = API_V2.CONTRACT_VIDEO_RECORDS.replace("{contractId}", `${contractId}`)
    const response = await Request.get<VideoRecord[]>(url)
    return response.data
  }

  const postContractDocumentApprovalReq = async (
    contractId: string | string[] | number,
    workflowId: string | string[] | number,
    payload: Params
  ) => {
    const url = API.CONTRACT_DOCUMENT_APPROVAL_REQUEST.replace(
      "{contractId}",
      `${contractId}`
    ).replace("{workflowId}", `${workflowId}`)
    const response = await Request.post(url, payload)
    return response.data
  }

  const getContractDocumentApprovalSend = async (
    contractId: string | string[],
    workflowId: string | string[]
  ) => {
    const url = API.CONTRACT_DOCUMENT_APPROVAL_SEND.replace(
      "{contractId}",
      `${contractId}`
    ).replace("{workflowId}", `${workflowId}`)
    const response = await Request.get<Contract>(url)
    return response.data
  }

  const getContractDocumentSign = async (
    contractId: string | string[],
    workflowId: string | string[]
  ) => {
    const url = API.CONTRACT_DOCUMENT_SIGN.replace("{contractId}", `${contractId}`).replace(
      "{workflowId}",
      `${workflowId}`
    )
    const response = await Request.get<Contract>(url)
    return response.data
  }

  const removeContractDocument = async (contractId: string | string[], params: object) => {
    const url = API.CONTRACT_DOCUMENT.replace("{contractId}", `${contractId}`)
    return await Request.del(url, params)
  }

  const updateContractStatus = async (contractId: string | string[], payload: object) => {
    const url = API.CONTRACT_STATUS.replace("{contractId}", `${contractId}`)
    const response = await Request.put(url, payload)
    return response.data
  }

  const postContractCheckTerm = async (contractId: string | string[]) => {
    const url = API.CONTRACT_CHECKED_TERM.replace("{contractId}", `${contractId}`)
    return await Request.post<boolean>(url)
  }

  const postContractSignBusiness = async (hash: string, payload: Params) => {
    const url = API.CONTRACT_SIGN_BUSINESS.replace("{hash}", hash)
    const response = await Request.post<boolean>(url, payload)
    return response.data
  }

  const getContractBusiness = async (contractId: string | string[]) => {
    const url = API_V2.CONTRACT_BUSINESS.replace("{contractId}", `${contractId}`)
    const response = await Request.get<SignContractWithListDocs>(url)
    return response.data
  }

  const getContractCorporateHousing = async (contractId: string | string[]) => {
    const url = API_V2.CORPORATE_HOUSING_CONTRACT.replace("{contractId}", `${contractId}`)
    const response = await Request.get<SignContractWithListDocs>(url)
    return response.data
  }

  const getOwnerAgencyContract = async (contractId: string | string[]) => {
    const url = API_V2.OWNER_AGENCY_CONTRACT.replace("{contractId}", `${contractId}`)
    const response = await Request.get<SignContractWithListDocs>(url)
    return response.data
  }

  const getCollectionAgencyContract = async (contractId: string | string[]) => {
    const url = API_V2.COLLECTION_AGENCY_CONTRACT.replace("{contractId}", `${contractId}`)
    const response = await Request.get<SignContractWithListDocs>(url)
    return response.data
  }

  const getContractVideoRoles = async (hash: string | string[]) => {
    const url = API.CONTRACT_VIDEO_ROLES.replace("{hash}", `${hash}`)
    return await Request.get<{ turnOn: boolean }>(url)
  }

  const putContractVideoRoles = async (hash: string | string[], payload: Params) => {
    const url = API.CONTRACT_VIDEO_ROLES.replace("{hash}", `${hash}`)
    return await Request.put(url, payload, null, false)
  }

  const getContractVideo = async (hash: string | string[]) => {
    const url = API.CONTRACT_VIDEO.replace("{hash}", `${hash}`)
    return await Request.get<OnlineSignContract>(url)
  }

  const postContractSign = async (hash: string, payload: Params) => {
    const url = API.CONTRACT_SIGN.replace("{hash}", `${hash}`)
    const response = await Request.post(url, payload)
    return response.data
  }

  const postContractDocumentSign = async (
    contractId: number,
    workflowId: number,
    payload: Params
  ) => {
    const url = API.CONTRACT_DOCUMENT_SIGN.replace("{contractId}", `${contractId}`).replace(
      "{workflowId}",
      `${workflowId}`
    )
    const response = await Request.post(url, payload)
    return response.data
  }

  const postContractDocumentApproval = async (
    contractId: number,
    workflowId: number,
    flag: boolean
  ) => {
    const url = API.CONTRACT_DOCUMENT_APPROVAL.replace("{contractId}", `${contractId}`)
      .replace("{workflowId}", `${workflowId}`)
      .replace("{flag}", flag ? "ok" : "ng")
    const response = await Request.post(url)
    return response.data
  }

  const postContractDocumentReceived = async (contractId: number, workflowId: number) => {
    const url = API.CONTRACT_DOCUMENT_RECEIVED.replace("{contractId}", `${contractId}`).replace(
      "{workflowId}",
      `${workflowId}`
    )
    const response = await Request.post<boolean | number>(url)
    return response.data
  }

  const getContracts = async (url: string, params: Params) => {
    const _url = API.CONTRACT_API_FLEX.replace("{url}", url)
    const response = await Request.get<ExTableDataSource>(_url, params, true)
    return response.data
  }

  const getLedgerMemoContract = async (contractId: number) => {
    const url = API.CONTRACT_LEDGER_HISTORY.replace("{contractId}", `${contractId}`)
    const response = await Request.get<LedgerHistory[]>(url)
    return response.data
  }

  const flexApiContracts = async (
    method: string,
    status: number,
    contractId: string | string[],
    params: Params
  ) => {
    const url = status === 60 ? API_V2.CONTRACT_DRAFT : API.CONTRACT
    const response = await Request[method]<any>(
      url.replace("{contractId}", `${contractId}`),
      params
    )
    return response.data
  }

  const postContractContractorConfirm = async (contractId: number, configs: Params, params: Params) => {
    const url = API.CONTRACTOR_CONFIRM.replace("{contractId}", `${contractId}`)
    return await Request.post<{ time: string }>(url, params, configs)
  }

  const contractDeleteFile = async (payload: Params) => {
    return await Request.post(API.CONTRACT_DELETE_FILE, payload)
  }

  const getContractAll = async (params: Params) => {
    return await Request.get<ExTableDataSource>(API.CONTRACT_ALL, params)
  }

  const putContractDraf = async (contractId: string | string[], payload: Params) => {
    const url = API_V2.CONTRACT_DRAFT.replace("{contractId}", `${contractId}`)
    return await Request.put<any>(url, payload)
  }

  const delContractDraf = async (payload: Params) => {
    return await Request.del(API.CONTRACT_DRAFT_DELETE, {}, false, payload)
  }

  const getContractCountDraf = async () => {
    return await Request.get<{ counts: number }>(API.CONTRACT_DRAFT_COUNT)
  }

  const getContractInsurances = async () => {
    return await Request.get<{ id: number; name: string; email: string }[]>(API.CONTRACT_INSURANCES)
  }

  const getItImportantDocumentRequiredCondCheck = async (contractId: string | number) => {
    const url = API_V2.CONTRACT_IT_IMPORTANT_DOCUMENT_REQUIRED_COND_CHECK.replace(
      "{contractId}",
      `${contractId}`
    )
    const response = await Request.get<{
      meetingDate: Date
      notUploadedContractDocuments: ContractDocument[]
      uploadedContractDocuments: ContractDocument[]
      notUploadedContractDocuments17: ContractDocument[]
      waitSendContractDocuments17: ContractDocument[]
      canSendContractDocuments17: ContractDocument[]
    }>(url)
    return response.data
  }

  const postLedgerStatus = async (data) => {
    const url = API.LEDGER_STATUS
    const response = await Request.post(url, data)
    return response.data
  }

  const acknowledgeDisabledDocs = async (contractId: string | number, contractDocumentIds: number[], guestType?: string) => {
    const url = API.ACKNOWLEDGE_DISABLED_DOCUMENTS.replace("{contractId}", `${contractId}`)
    const response = await Request.post<boolean>(url, { contractDocumentIds, guestType })
    return response.data
  }

  const tabData: { title: string; status: number | null; columns: TableColumn[] }[] = [
    {
      title: "契約準備中",
      status: 0,
      columns: [
        { title: "No", key: "id", size: "s" },
        {
          title: "契約番号",
          key: "contractCode",
          render(data: any) {
            return Number(data.contractCode) || "ー"
          },
        },
        { title: "契約者名", key: "name" },
        { title: "入居者名", key: "residentName" },
        {
          title: "物件名・部屋番号",
          key: "propertyFullName",
          render(data: any) {
            return `${data.propertyName} ${data.propertyRoomNumber}`
          },
        },
        {
          title: "管理会社名・担当者",
          key: "managementCompanyName",
          render(data: any) {
            return `${data.managementCompanyName} ${data.managementUserName}`
          },
        },
        {
          title: "仲介会社名・担当者",
          key: "agentCompanyName",
          render(data: any) {
            return `${data.agentCompanyName} ${data.agentUserName}`
          },
        },
        {
          title: "重説日時",
          key: "meetingDate",
          render(data: any) {
            return data.meetingDate ? (
              <>
                {dayjs(data.meetingDate).format("YYYY/MM/DD")}
                <br />
                {dayjs(data.meetingDate).format("HH:mm")}
              </>
            ) : (
              <span style={{ color: "red" }}>未定</span>
            )
          },
        },
        {
          title: "ステータス",
          key: "status",
          render(data: any) {
            return <TaskTag status={data.status} />
          },
        },
      ],
    },
    {
      title: "重説準備中",
      status: 10,
      columns: [
        { title: "No", key: "id", size: "s" },
        {
          title: "契約番号",
          key: "contractCode",
          render(data: any) {
            return Number(data.contractCode) || "ー"
          },
        },
        { title: "契約者名", key: "name" },
        { title: "入居者名", key: "residentName" },
        {
          title: "物件名・部屋番号",
          key: "propertyFullName",
          render(data: any) {
            return `${data.propertyName} ${data.propertyRoomNumber}`
          },
        },
        {
          title: "管理会社名・担当者",
          key: "managementCompanyName",
          render(data: any) {
            return `${data.managementCompanyName} ${data.managementUserName}`
          },
        },
        {
          title: "仲介会社名・担当者",
          key: "agentCompanyName",
          render(data: any) {
            return `${data.agentCompanyName} ${data.agentUserName}`
          },
        },
        {
          title: "重説日時",
          key: "meetingDate",
          render(data: any) {
            return data.meetingDate ? (
              <>
                {dayjs(data.meetingDate).format("YYYY/MM/DD")}
                <br />
                {dayjs(data.meetingDate).format("HH:mm")}
              </>
            ) : (
              <span style={{ color: "red" }}>未定</span>
            )
          },
        },
        {
          title: "ステータス",
          key: "status",
          render(data: any) {
            return <TaskTag status={data.status} />
          },
        },
      ],
    },
    {
      title: "重説準備完了",
      status: 20,
      columns: [
        { title: "No", key: "id", size: "s" },
        {
          title: "契約番号",
          key: "contractCode",
          render(data: any) {
            return Number(data.contractCode) || "ー"
          },
        },
        { title: "契約者名", key: "name" },
        { title: "入居者名", key: "residentName" },
        {
          title: "物件名・部屋番号",
          key: "propertyFullName",
          render(data: any) {
            return `${data.propertyName} ${data.propertyRoomNumber}`
          },
        },
        {
          title: "管理会社名・担当者",
          key: "managementCompanyName",
          render(data: any) {
            return `${data.managementCompanyName} ${data.managementUserName}`
          },
        },
        {
          title: "仲介会社名・担当者",
          key: "agentCompanyName",
          render(data: any) {
            return `${data.agentCompanyName} ${data.agentUserName}`
          },
        },
        {
          title: "重説日時",
          key: "meetingDate",
          render(data: any) {
            return data.meetingDate ? (
              <>
                {dayjs(data.meetingDate).format("YYYY/MM/DD")}
                <br />
                {dayjs(data.meetingDate).format("HH:mm")}
              </>
            ) : (
              <span style={{ color: "red" }}>未定</span>
            )
          },
        },
        {
          title: "ステータス",
          key: "status",
          render(data: any) {
            return <TaskTag status={data.status} />
          },
        },
      ],
    },
    {
      title: "IT重説完了",
      status: 21,
      columns: [
        { title: "No", key: "id", size: "s" },
        {
          title: "契約番号",
          key: "contractCode",
          render(data: any) {
            return Number(data.contractCode) || "ー"
          },
        },
        { title: "契約者名", key: "name" },
        { title: "入居者名", key: "residentName" },
        {
          title: "物件名・部屋番号",
          key: "propertyFullName",
          render(data: any) {
            return `${data.propertyName} ${data.propertyRoomNumber}`
          },
        },
        {
          title: "管理会社名・担当者",
          key: "managementCompanyName",
          render(data: any) {
            return `${data.managementCompanyName} ${data.managementUserName}`
          },
        },
        {
          title: "仲介会社名・担当者",
          key: "agentCompanyName",
          render(data: any) {
            return `${data.agentCompanyName} ${data.agentUserName}`
          },
        },
        {
          title: "重説日時",
          key: "meetingDate",
          render(data: any) {
            return data.meetingDate ? (
              <>
                {dayjs(data.meetingDate).format("YYYY/MM/DD")}
                <br />
                {dayjs(data.meetingDate).format("HH:mm")}
              </>
            ) : (
              <span style={{ color: "red" }}>未定</span>
            )
          },
        },
        {
          title: "ステータス",
          key: "status",
          render(data: any) {
            return <TaskTag status={data.status} />
          },
        },
      ],
    },
    {
      title: "契約済",
      status: 30,
      columns: [
        { title: "No", key: "id", size: "s" },
        {
          title: "契約番号",
          key: "contractCode",
          render(data: any) {
            return Number(data.contractCode) || "ー"
          },
        },
        { title: "契約者名", key: "name" },
        { title: "入居者名", key: "residentName" },
        {
          title: "物件名・部屋番号",
          key: "propertyFullName",
          render(data: any) {
            return `${data.propertyName} ${data.propertyRoomNumber}`
          },
        },
        {
          title: "管理会社名・担当者",
          key: "managementCompanyName",
          render(data: any) {
            return `${data.managementCompanyName} ${data.managementUserName}`
          },
        },
        {
          title: "仲介会社名・担当者",
          key: "agentCompanyName",
          render(data: any) {
            return `${data.agentCompanyName} ${data.agentUserName}`
          },
        },
        {
          title: "重説日時",
          key: "meetingDate",
          render(data: any) {
            return data.meetingDate ? (
              <>
                {dayjs(data.meetingDate).format("YYYY/MM/DD")}
                <br />
                {dayjs(data.meetingDate).format("HH:mm")}
              </>
            ) : (
              <span style={{ color: "red" }}>未定</span>
            )
          },
        },
        // {
        //   title: "契約書類",
        //   key: "",
        //   render(data: any) {
        //     const onDownload = (downloadData: DownloadData) => {
        //       showDialog(
        //         `${downloadData.workflowName}のダウンロード`,
        //         <Download downloadData={downloadData} innerLoading={true} />,
        //         null
        //       ).then()
        //     }
        //     return data.documents?.map((item) => (
        //       <>
        //         <a onClick={onDownload.bind(this, item)}>{item.workflowName}</a>
        //         <br />
        //       </>
        //     ))
        //   },
        // },
        {
          title: "ステータス",
          key: "status",
          render(data: any) {
            return <TaskTag status={data.status} />
          },
        },
      ],
    },

    {
      title: "契約完了",
      status: 40,
      columns: [
        { title: "No", key: "id", size: "s" },
        {
          title: "契約番号",
          key: "contractCode",
          render(data: any) {
            return Number(data.contractCode) || "ー"
          },
        },
        { title: "契約者名", key: "name" },
        {
          title: "物件名・部屋番号",
          key: "propertyFullName",
          render(data: any) {
            return `${data.propertyName} ${data.propertyRoomNumber}`
          },
        },
        {
          title: "管理会社名・担当者",
          key: "managementCompanyName",
          render(data: any) {
            return `${data.managementCompanyName} ${data.managementUserName}`
          },
        },
        {
          title: "仲介会社名・担当者",
          key: "agentCompanyName",
          render(data: any) {
            return (
              <span
                style={{ whiteSpace: "pre-wrap" }}
              >{`${data.agentCompanyName} ${data.agentUserName}`}</span>
            )
          },
        },
        {
          title: "契約日時",
          key: "contractorSignedAt",
          render(data) {
            const time = data.contractorSignedAt
            return time ? (
              <>
                {dayjs(time).format("YYYY/MM/DD")}
                <br />
                {dayjs(time).format("HH:mm")}
              </>
            ) : (
              <span style={{ color: "red" }}>-</span>
            )
          },
        },
        {
          title: "取引金額",
          key: "price",
          render(data) {
            return Number(data.price) ? formatPrice(data.price) + "円" : "0"
          },
        },
      ],
    },

    {
      title: "キャンセル",
      status: 50,
      columns: [
        { title: "No", key: "id", size: "s" },
        {
          title: "契約番号",
          key: "contractCode",
          render(data: any) {
            return Number(data.contractCode) || "ー"
          },
        },
        { title: "契約者名", key: "name" },
        { title: "入居者名", key: "residentName" },
        {
          title: "物件名・部屋番号",
          key: "propertyFullName",
          render(data: any) {
            return `${data.propertyName} ${data.propertyRoomNumber}`
          },
        },
        {
          title: "管理会社名・担当者",
          key: "managementCompanyName",
          render(data: any) {
            return `${data.managementCompanyName} ${data.managementUserName}`
          },
        },
        {
          title: "仲介会社名・担当者",
          key: "agentCompanyName",
          render(data: any) {
            return `${data.agentCompanyName} ${data.agentUserName}`
          },
        },
        {
          title: "キャンセル日時",
          key: "canceledDate",
          render(data: any) {
            return data.canceledDate ? (
              <>
                {dayjs(data.canceledDate).format("YYYY/MM/DD")}
                <br />
                {dayjs(data.canceledDate).format("HH:mm")}
              </>
            ) : (
              <span style={{ color: "red" }}>-</span>
            )
          },
        },
      ],
    },
    {
      title: "下書き",
      status: 60,
      columns: [
        { title: "No", key: "id", size: "s" },
        {
          title: "契約番号",
          key: "contractCode",
          render(data: any) {
            return Number(data.contractCode) || "ー"
          },
        },
        { title: "契約者名", key: "name" },
        {
          title: "物件名・部屋番号",
          key: "propertyFullName",
          render(data: any) {
            return `${data.propertyName} ${data.propertyRoomNumber}`
          },
        },
        {
          title: "取込日時",
          key: "createdAt",
          render(data: any) {
            return data.createdAt ? (
              <>
                {dayjs(data.createdAt).format("YYYY/MM/DD")}
                <br />
                {dayjs(data.createdAt).format("HH:mm")}
              </>
            ) : (
              <span style={{ color: "red" }}>未定</span>
            )
          },
        },
        {
          title: "更新日時",
          key: "updatedAt",
          render(data: any) {
            return data.updatedAt ? (
              <>
                {dayjs(data.updatedAt).format("YYYY/MM/DD")}
                <br />
                {dayjs(data.updatedAt).format("HH:mm")}
              </>
            ) : (
              <span style={{ color: "red" }}>未定</span>
            )
          },
        },
        { title: "取込作業者", key: "imported_by" },
        {
          title: "ステータス",
          key: "status",
          render(data: any) {
            return <TaskTag status={data.status} />
          },
        },
      ],
    },
    {
      title: "Need JRD",
      status: 69,
      columns: [
        { title: "No", key: "id", size: "s" },
        {
          title: "契約番号",
          key: "contractCode",
          render(data: any) {
            return Number(data.contractCode) || "ー"
          },
        },
        { title: "契約者名", key: "name" },
        { title: "入居者名", key: "residentName" },
        {
          title: "物件名・部屋番号",
          key: "propertyFullName",
          render(data: any) {
            return `${data.propertyName} ${data.propertyRoomNumber}`
          },
        },
        {
          title: "管理会社名・担当者",
          key: "managementCompanyName",
          render(data: any) {
            return `${data.managementCompanyName} ${data.managementUserName}`
          },
        },
        {
          title: "仲介会社名・担当者",
          key: "agentCompanyName",
          render(data: any) {
            return `${data.agentCompanyName} ${data.agentUserName}`
          },
        },
        {
          title: "重説日時",
          key: "meetingDate",
          render(data: any) {
            return data.meetingDate ? (
              <>
                {dayjs(data.meetingDate).format("YYYY/MM/DD")}
                <br />
                {dayjs(data.meetingDate).format("HH:mm")}
              </>
            ) : (
              <span style={{ color: "red" }}>未定</span>
            )
          },
        },
        {
          title: "ステータス",
          key: "status",
          render(data: any) {
            return <TaskTag status={data.status} />
          },
        },
      ],
    },
    {
      title: "",
      status: 100,
      columns: [
        { title: "No", key: "id", size: "s" },
        {
          title: "契約番号",
          key: "contractCode",
          render(data: any) {
            return Number(data.contractCode) || "ー"
          },
        },
        { title: "契約者名", key: "name" },
        {
          title: "物件名・部屋番号",
          key: "propertyFullName",
          render(data: any) {
            return `${data.propertyName} ${data.propertyRoomNumber}`
          },
        },
        {
          title: "管理会社名・担当者",
          key: "managementCompanyName",
          render(data: any) {
            return `${data.managementCompanyName || "ー"} ${data.managementUserName || ""}`
          },
        },
        {
          title: "仲介会社名・担当者",
          key: "agentCompanyName",
          render(data: any) {
            return `${data.agentCompanyName || "ー"} ${data.agentUserName || ""}`
          },
        },

        {
          title: "ステータス",
          key: "status",
          render(data: any) {
            return <TaskTag status={data.status} />
          },
        },
      ],
    },

    {
      title: "全て",
      status: null,
      columns: [
        { title: "No", key: "id", size: "s" },
        {
          title: "契約番号",
          key: "contractCode",
          render(data: any) {
            return Number(data.contractCode) || "ー"
          },
        },
        { title: "契約者名", key: "name" },
        { title: "入居者名", key: "residentName" },
        {
          title: "物件名・部屋番号",
          key: "propertyFullName",
          render(data: any) {
            return `${data.propertyName} ${data.propertyRoomNumber}`
          },
        },
        {
          title: "管理会社名・担当者",
          key: "managementCompanyName",
          render(data: any) {
            return `${data.managementCompanyName} ${data.managementUserName}`
          },
        },
        {
          title: "仲介会社名・担当者",
          key: "agentCompanyName",
          render(data: any) {
            return `${data.agentCompanyName} ${data.agentUserName}`
          },
        },
        {
          title: "重説日時",
          key: "meetingDate",
          render(data: any) {
            return data.meetingDate ? (
              <>
                {dayjs(data.meetingDate).format("YYYY/MM/DD")}
                <br />
                {dayjs(data.meetingDate).format("HH:mm")}
              </>
            ) : (
              <span style={{ color: "red" }}>未定</span>
            )
          },
        },

        {
          title: "ステータス",
          key: "status",
          render(data: any) {
            return <TaskTag status={data.status} />
          },
        },
      ],
    },
  ]

  const ledgerStatusSelections = [
    {
      title: "借主側サイン済",
      status: 1 //contructor signed
    },
    {
      title: "1次済不備アリ",
      status: 2 // first signed with issues
    },
    {
      title: "1次済不備ナシ",
      status: 3 // first signed without issues
    }
  ]

  const contractTabData = [
    {
      title: "現在進行中",
      pathname: "/contracts/progress/",
    },
    {
      title: "契約完了",
      pathname: "/contracts/complete/",
    },
    {
      title: "キャンセル",
      pathname: "/contracts/cancel/",
    },
  ]

  const ledgerTabData: { title: string; pathname: string; columns: TableColumn[] }[] = [
    {
      title: "現在進行中",
      pathname: "/contracts/ledger/progress/",
      columns: [
        {
          title: "契約番号",
          key: "contractCode",
          order: true,
          render(data: any) {
            return Number(data.contractCode) || "ー"
          },
        },
        { 
          title: "管理形態", 
          key: "contractType",
          order: true,
          render(data: any) {
            return data.contractType != null ? ContractTypeTexts[data.contractType] : "ー" 
          }
        },
        { title: "契約者名", key: "name", order: true },
        {
          title: "物件名・部屋番号",
          key: "propertyFullName",
          order: true,
          render(data: any) {
            return `${data.propertyName} ${data.propertyRoomNumber}`
          },
        },
        {
          title: "仲介会社名・担当者",
          key: "agentCompanyName",
          order: true,
          render(data: any) {
            if (data.agentCompanyName != null && data.agentUserName != null) {
              return `${data.agentCompanyName} ${data.agentUserName}`
            } else {
              return "ー"
            }
          },
        },
        {
          title: "借主側サイン日時",
          key: "lastestSignedTime",
          order: true,
          render(data: any) {
            return data.lastestSignedTime ? (
              <>
                {dayjs(data.lastestSignedTime).format("YYYY/MM/DD")}
                <br />
                {dayjs(data.lastestSignedTime).format("HH:mm")}
              </>
            ) : "ー"
          },
        },
        {
          title: "チェック日時",
          key: "ledgerCheckedTime",
          order: true,
          render(data: any) {
            return data.ledgerCheckedTime != null ? (
              <>
                {dayjs(data.ledgerCheckedTime).format("YYYY/MM/DD")}
                <br />
                {dayjs(data.ledgerCheckedTime).format("HH:mm")}
              </>
            ) : (
              "ー"
            )
          },
        },
        {
          title: "チェック担当者",
          key: "ledgerCheckedUser",
          order: true,
          render(data: any) {
            return data.ledgerCheckedUser != null ? data.ledgerCheckedUser : "ー"
          },
        },
        {
          title: "メモ",
          key: "memoList",
          order: false,
          render(data: any) {
            return data.hasMemo ? (
              <>
                <img
                  src={"/assets/svg/Memo.svg"}
                  alt="memo"
                  width={25}
                  height={30}
                  className="mx-auto"
                />
              </>
            ) : "ー"
          },
        },
        {
          title: "契約ステータス",
          key: "status",
          render(data: any) {
            return <TaskTag status={data.status} />
          },
        },
      ],
    },
    {
      title: "承認完了",
      pathname: "/contracts/ledger/completed/",
      columns: [
        {
          title: "契約番号",
          key: "contractCode",
          order: true,
          render(data: any) {
            return Number(data.contractCode) || "ー"
          },
        },
        { 
          title: "管理形態", 
          key: "contractType",
          order: true,
          render(data: any) {
            return data.contractType != null ? ContractTypeTexts[data.contractType] : "ー" 
          }
        },
        { title: "契約者名", key: "name", order: true },
        {
          title: "物件名・部屋番号",
          key: "propertyFullName",
          order: true,
          render(data: any) {
            return `${data.propertyName} ${data.propertyRoomNumber}`
          },
        },
        {
          title: "仲介会社名・担当者",
          key: "agentCompanyName",
          order: true,
          render(data: any) {
            if (data.agentCompanyName != null && data.agentUserName != null) {
              return `${data.agentCompanyName} ${data.agentUserName}`
            } else {
              return "ー"
            }
          },
        },
        {
          title: "借主側サイン日時",
          key: "lastestSignedTime",
          order: true,
          render(data: any) {
            return data.lastestSignedTime ? (
              <>
                {dayjs(data.lastestSignedTime).format("YYYY/MM/DD")}
                <br />
                {dayjs(data.lastestSignedTime).format("HH:mm")}
              </>
            ) : "ー"
          },
        },
        {
          title: "チェック日時",
          key: "ledgerCheckedTime",
          order: true,
          render(data: any) {
            return (
              <>
                {dayjs(data.ledgerCheckedTime).format("YYYY/MM/DD")}
                <br />
                {dayjs(data.ledgerCheckedTime).format("HH:mm")}
              </>
            )
          },
        },
        {
          title: "承認日時",
          key: "ledgerApprovedTime",
          order: true,
          render(data: any) {
            return (
              <>
                {dayjs(data.ledgerApprovedTime).format("YYYY/MM/DD")}
                <br />
                {dayjs(data.ledgerApprovedTime).format("HH:mm")}
              </>
            )
          },
        },
        {
          title: "メモ",
          key: "memoList",
          order: false,
          render(data: any) {
            return data.hasMemo ? (
              <>
                <img
                  src={"/assets/svg/Memo.svg"}
                  alt="memo"
                  width={25}
                  height={30}
                  className="mx-auto"
                />
              </>
            ) : "ー"
          },
        },
      ],
    },
  ]
  

  const statusDataSource = (user: User) => {
    const data: any[] = [
      [
        {
          title: "ステータス",
          contents: [
            {
              objectId: "status",
              element: "Radio",
              data: {
                error: "ステータス",
                defaultChecked: 0,
                dataSource: [
                  {
                    label: "現在進行中",
                    value: 0,
                  },
                ].concat(
                  (config.getConfig("contractStatus") as any).filter((item) => item.value === 50)
                ),
              },
            },
          ],
        },
      ],
    ]

    if (user?.company?.type === CompanyType.MANAGEMENT) {
      data.push([
        {
          title: "契約完了日時",
          isHide: (data: any) => data.status === 40,
          contents: [
            {
              element: "DatePicker",
              objectId: "completedDate",
              data: {
                error: "完了日",
                placeholder: "日時を選択",
                validations: { required: true },
              },
            },
            {
              element: "TimePicker",
              objectId: "completedDate",
              data: {
                error: "完了時間",
                placeholder: "時間を選択",
                validations: { required: true },
              },
            },
          ],
        },
      ])
    }

    data.push(
      ...[
        [
          {
            title: "キャンセル日時",
            isHide: (data: any) => data.status === 50,
            contents: [
              {
                element: "DatePicker",
                objectId: "canceledDate",
                data: {
                  name: "canceledDate",
                  error: "キャンセル日時",
                  placeholder: "日時を選択",
                  validations: { required: true },
                },
              },
              {
                element: "TimePicker",
                objectId: "canceledDate",
                data: {
                  name: "canceledDateTime",
                  error: "キャンセル時間",
                  placeholder: "時間を選択",
                  validations: { required: true },
                },
              },
            ],
          },
        ],
        [
          {
            title: "キャンセル理由",
            isHide: (data: any) => data.status === 50,
            contents: [
              {
                element: "Select",
                objectId: "cancelReason",
                data: {
                  error: "キャンセル理由",
                  validations: { required: true },
                  dataSource: config.getConfig("cancelReason"),
                },
              },
            ],
          },
        ],
      ]
    )
    return data
  }

  return {
    onSave,
    saveMeetingDate,
    acknowledgeDisabledDocs,
    saveMeetingDateV2,
    sendDocument,
    resendDocumentsV2,
    reSendDocument,
    saveDocument,
    saveDocumentMulti,
    statusDataSource,
    tabData,
    contractTabData,
    ledgerTabData,
    ledgerStatusSelections,
    getContract,
    getOwnerAgencyContract,
    getCollectionAgencyContract,
    getContractDocumentApprovalSend,
    removeContractDocument,
    updateContractStatus,
    postContractCheckTerm,
    getContractBusiness,
    getContractCorporateHousing,
    postContractSignBusiness,
    getContractDocumentSign,
    getContractToken,
    getContractVideoRoles,
    getContractVideo,
    contractDeleteFile,
    getContractAll,
    postContractContractorConfirm,
    getContractCountDraf,
    putContractDraf,
    delContractDraf,
    postContractSign,
    postContractDocumentSign,
    postContractDocumentApproval,
    postContractDocumentReceived,
    postContractDocumentApprovalReq,
    getDocumentSend,
    putContractVideoRoles,
    getContracts,
    getContractInsurances,
    flexApiContracts,
    getItImportantDocumentRequiredCondCheck,
    postLedgerStatus,
    updateResonaStatus,
    remailResona,
    getContractVideoRecords,
    getLedgerMemoContract,
  }
}
